import { useEffect, useState, useCallback } from 'react'
import { useAuth } from '../../context/AuthContext'
import { supabase } from '../../supabaseClient'
import { Plus, X, Search, Mail, Phone, Building2 } from 'lucide-react'

const EMPTY = { nome: '', cognome: '', email: '', telefono: '', azienda: '', ruolo: '', note: '' }

const iniziale = (c) => ((c.nome || '?')[0] + (c.cognome ? c.cognome[0] : '')).toUpperCase()

const COLORS = ['bg-violet-600', 'bg-blue-600', 'bg-emerald-600', 'bg-rose-600', 'bg-amber-600', 'bg-sky-600']
const avatarColor = (id) => COLORS[(id?.charCodeAt(0) ?? 0) % COLORS.length]

// ─── ContattoModal ─────────────────────────────────────────────────────────────
const ContattoModal = ({ contatto, onClose, onSaved, onDeleted }) => {
  const { profile } = useAuth()
  const isEdit = !!contatto?.id
  const [form, setForm] = useState(isEdit ? { ...contatto } : { ...EMPTY })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.nome.trim()) return
    setLoading(true)
    setError(null)
    const payload = {
      nome: form.nome.trim(),
      cognome: form.cognome?.trim() || null,
      email: form.email?.trim() || null,
      telefono: form.telefono?.trim() || null,
      azienda: form.azienda?.trim() || null,
      ruolo: form.ruolo?.trim() || null,
      note: form.note?.trim() || null,
    }
    let data, err
    if (isEdit) {
      ;({ data, error: err } = await supabase.from('contatti').update(payload).eq('id', contatto.id).select().single())
    } else {
      ;({ data, error: err } = await supabase.from('contatti').insert({ ...payload, created_by: profile.id }).select().single())
    }
    if (err) { setError(err.message); setLoading(false); return }
    onSaved(data)
    onClose()
  }

  const handleDelete = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return }
    await supabase.from('contatti').delete().eq('id', contatto.id)
    onDeleted(contatto.id)
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4 py-6">
      <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gray-900 border-b border-gray-800 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <h2 className="text-lg font-bold text-white">{isEdit ? 'Modifica contatto' : 'Nuovo contatto'}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Nome *</label>
              <input value={form.nome} onChange={set('nome')} required autoFocus
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-violet-500 transition-colors"
                placeholder="Mario" />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Cognome</label>
              <input value={form.cognome} onChange={set('cognome')}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-violet-500 transition-colors"
                placeholder="Rossi" />
            </div>
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Email</label>
            <input type="email" value={form.email} onChange={set('email')}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-violet-500 transition-colors"
              placeholder="mario@esempio.it" />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Telefono</label>
            <input value={form.telefono} onChange={set('telefono')}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-violet-500 transition-colors"
              placeholder="+39 333 000 0000" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Azienda</label>
              <input value={form.azienda} onChange={set('azienda')}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-violet-500 transition-colors"
                placeholder="Acme Srl" />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Ruolo</label>
              <input value={form.ruolo} onChange={set('ruolo')}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-violet-500 transition-colors"
                placeholder="Direttore creativo" />
            </div>
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Note</label>
            <textarea value={form.note} onChange={set('note')} rows={3}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-violet-500 transition-colors resize-none"
              placeholder="Note interne..." />
          </div>
          {error && <p className="text-red-400 text-sm">{error}</p>}
          <div className="flex gap-3 pt-2">
            {isEdit && (
              <button type="button" onClick={handleDelete}
                className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  confirmDelete
                    ? 'bg-red-500/20 text-red-400 border border-red-500/40 hover:bg-red-500 hover:text-white'
                    : 'border border-gray-700 text-gray-500 hover:text-red-400 hover:border-red-500/40'
                }`}>
                {confirmDelete ? 'Conferma eliminazione' : 'Elimina'}
              </button>
            )}
            <button type="button" onClick={onClose}
              className="flex-1 py-3 rounded-lg border border-gray-700 text-gray-400 hover:text-white transition-colors text-sm">
              Annulla
            </button>
            <button type="submit" disabled={loading || !form.nome.trim()}
              className="flex-1 py-3 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white font-medium text-sm transition-colors">
              {loading ? 'Salvataggio...' : isEdit ? 'Salva' : 'Aggiungi'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Rubrica ───────────────────────────────────────────────────────────────────
const Rubrica = () => {
  const [contatti, setContatti] = useState([])
  const [loading, setLoading] = useState(true)
  const [query, setQuery] = useState('')
  const [modalContatto, setModalContatto] = useState(null)
  const [showModal, setShowModal] = useState(false)

  const fetchContatti = useCallback(async () => {
    const { data } = await supabase.from('contatti').select('*').order('nome')
    setContatti(data || [])
    setLoading(false)
  }, [])

  useEffect(() => {
    fetchContatti()
    const channel = supabase
      .channel('rubrica-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'contatti' }, fetchContatti)
      .subscribe()
    return () => supabase.removeChannel(channel)
  }, [fetchContatti])

  const handleSaved = (saved) => {
    setContatti(prev => {
      const exists = prev.find(c => c.id === saved.id)
      const aggiornata = exists
        ? prev.map(c => c.id === saved.id ? saved : c)
        : [...prev, saved]
      return aggiornata.sort((a, b) => a.nome.localeCompare(b.nome))
    })
  }

  const handleDeleted = (id) => setContatti(prev => prev.filter(c => c.id !== id))

  const filtered = contatti.filter(c => {
    const q = query.toLowerCase()
    return (
      c.nome?.toLowerCase().includes(q) ||
      c.cognome?.toLowerCase().includes(q) ||
      c.azienda?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q) ||
      c.ruolo?.toLowerCase().includes(q)
    )
  })

  const gruppi = {}
  filtered.forEach(c => {
    const lettera = (c.nome?.[0] || '#').toUpperCase()
    if (!gruppi[lettera]) gruppi[lettera] = []
    gruppi[lettera].push(c)
  })

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
    </div>
  )

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Rubrica</h1>
          <p className="text-gray-500 text-sm mt-1">{contatti.length} contatti</p>
        </div>
        <button
          onClick={() => { setModalContatto({}); setShowModal(true) }}
          className="flex items-center gap-2 bg-violet-600 hover:bg-violet-500 text-white font-medium px-4 py-2.5 rounded-lg transition-colors text-sm"
        >
          <Plus size={16} />
          Nuovo
        </button>
      </div>

      <div className="relative mb-6">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Cerca per nome, azienda, email..."
          className="w-full bg-gray-900 border border-gray-800 rounded-xl pl-10 pr-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-violet-500 transition-colors"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-24">
          <p className="text-gray-600 mb-4">{query ? 'Nessun risultato.' : 'Nessun contatto ancora.'}</p>
          {!query && (
            <button
              onClick={() => { setModalContatto({}); setShowModal(true) }}
              className="text-violet-400 hover:text-violet-300 text-sm transition-colors"
            >
              Aggiungi il primo contatto →
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {Object.keys(gruppi).sort().map(lettera => (
            <div key={lettera}>
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2 px-1">{lettera}</p>
              <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden divide-y divide-gray-800">
                {gruppi[lettera].map(c => (
                  <div
                    key={c.id}
                    onClick={() => { setModalContatto(c); setShowModal(true) }}
                    className="flex items-center gap-4 px-4 py-3 hover:bg-gray-800/40 transition-colors cursor-pointer"
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0 ${avatarColor(c.id)}`}>
                      {iniziale(c)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">
                        {c.nome}{c.cognome ? ` ${c.cognome}` : ''}
                      </p>
                      <p className="text-gray-500 text-xs truncate">
                        {[c.ruolo, c.azienda].filter(Boolean).join(' · ') || '—'}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      {c.email && (
                        <a href={`mailto:${c.email}`} onClick={e => e.stopPropagation()}
                          className="text-gray-600 hover:text-violet-400 transition-colors">
                          <Mail size={16} />
                        </a>
                      )}
                      {c.telefono && (
                        <a href={`tel:${c.telefono}`} onClick={e => e.stopPropagation()}
                          className="text-gray-600 hover:text-violet-400 transition-colors">
                          <Phone size={16} />
                        </a>
                      )}
                      {c.azienda && <Building2 size={14} className="text-gray-700" />}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <ContattoModal
          contatto={modalContatto}
          onClose={() => { setShowModal(false); setModalContatto(null) }}
          onSaved={handleSaved}
          onDeleted={handleDeleted}
        />
      )}
    </div>
  )
}

export default Rubrica
